import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { formatInr } from '../lib/formatInr';
import { displayAmount } from '../lib/bookingAmount';
import { MapPin, Route, AlertTriangle, RefreshCw, Package } from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const CITIES = ['Bangalore', 'Chennai', 'Hyderabad'];

const depotIcon = L.divIcon({
  html: `<div style="background:#000;border:3px solid #FF5A1F;width:40px;height:40px;border-radius:0;display:flex;align-items:center;justify-content:center;font-weight:900;font-family:'IBM Plex Mono',monospace;font-size:11px;color:#FF5A1F;box-shadow:3px 3px 0px #0A0A0A;">HUB</div>`,
  className: '', iconSize: [40, 40], iconAnchor: [20, 20],
});

const stopIcon = (n) => L.divIcon({
  html: `<div style="background:#FFB800;border:2px solid #000;width:34px;height:34px;border-radius:0;display:flex;align-items:center;justify-content:center;font-weight:900;font-family:'IBM Plex Mono',monospace;font-size:13px;color:#000;box-shadow:3px 3px 0px #0A0A0A;">${n}</div>`,
  className: '', iconSize: [34, 34], iconAnchor: [17, 17],
});

const FitToPoints = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    if (!positions.length) return;
    const bounds = L.latLngBounds(positions);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, positions]);
  return null;
};

const AdminPickupRouteMap = () => {
  const [city, setCity] = useState('Bangalore');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRoute = async (chosen) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/pickup-route?city=${encodeURIComponent(chosen)}`);
      setData(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load route');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoute(city); /* eslint-disable-next-line */ }, [city]);

  const stops = data?.stops || [];
  const depot = data?.depot;
  const polyline = depot && stops.length
    ? [[depot.lat, depot.lng], ...stops.map(s => [s.lat, s.lng]), [depot.lat, depot.lng]]
    : [];
  const allPositions = polyline;

  return (
    <div className="space-y-4" data-testid="admin-pickup-route">
      {/* Toolbar */}
      <div className="card-brutalist p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="data-label">CITY</span>
          <div className="flex gap-1" data-testid="pickup-city-filter">
            {CITIES.map(c => (
              <button
                key={c}
                data-testid={`pickup-city-btn-${c.toLowerCase()}`}
                onClick={() => setCity(c)}
                className={`px-4 py-2 border-2 border-black text-xs font-mono uppercase font-bold transition-colors ${
                  city === c ? 'bg-[#FF5A1F] text-black shadow-[3px_3px_0px_#0A0A0A]' : 'bg-white hover:bg-gray-100'
                }`}
              >{c}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap text-xs font-mono">
          {data && (
            <>
              <span><Package size={12} className="inline mr-1" /><b>{data.pending_count}</b> pending</span>
              <span><Route size={12} className="inline mr-1" /><b>{data.total_distance_km} km</b> optimised route</span>
              {data.ungeocoded?.length > 0 && (
                <span className="text-[#C62828]"><AlertTriangle size={12} className="inline mr-1" /><b>{data.ungeocoded.length}</b> un-geocoded</span>
              )}
            </>
          )}
          <button
            data-testid="pickup-refresh-btn"
            onClick={() => fetchRoute(city)}
            disabled={loading}
            className="btn-secondary px-3 py-2 flex items-center gap-2 text-xs disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            REFRESH
          </button>
        </div>
      </div>

      {error && (
        <div className="card-brutalist p-4 bg-red-50 border-red-500 text-red-700 text-sm font-mono">
          {error}
        </div>
      )}

      {/* Map */}
      <div className="card-brutalist p-0 overflow-hidden">
        <div className="px-5 py-3 border-b-2 border-black bg-white flex items-center gap-2">
          <MapPin size={16} />
          <h3 className="font-mono uppercase text-sm font-bold">OPTIMISED PICKUP ROUTE — {city.toUpperCase()}</h3>
        </div>
        <div style={{ height: '500px', width: '100%' }}>
          <MapContainer
            center={depot ? [depot.lat, depot.lng] : [12.9716, 77.5946]}
            zoom={11}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {depot && (
              <Marker position={[depot.lat, depot.lng]} icon={depotIcon}>
                <Popup><strong>{city} HUB</strong><br />Depot / start point</Popup>
              </Marker>
            )}
            {stops.map(s => (
              <Marker key={s.booking_id} position={[s.lat, s.lng]} icon={stopIcon(s.stop_number)}>
                <Popup>
                  <strong>#{s.stop_number} · {s.customer_name}</strong><br />
                  Tag: {s.baggage_tag}<br />
                  {s.pickup_address}<br />
                  <span style={{color:'#525252'}}>{formatInr(displayAmount(s))} · {s.tracking_status}</span>
                </Popup>
              </Marker>
            ))}
            {polyline.length > 1 && (
              <Polyline positions={polyline} color="#FF5A1F" weight={3} opacity={0.75} />
            )}
            <FitToPoints positions={allPositions} />
          </MapContainer>
        </div>
      </div>

      {/* Stop list */}
      <div className="card-brutalist p-0 overflow-hidden">
        <div className="px-5 py-3 border-b-2 border-black bg-white">
          <h3 className="font-mono uppercase text-sm font-bold">STOP SEQUENCE</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 border-b-2 border-black">
              <tr>
                <th className="text-left p-3 font-mono uppercase text-xs">#</th>
                <th className="text-left p-3 font-mono uppercase text-xs">Tag</th>
                <th className="text-left p-3 font-mono uppercase text-xs">Customer</th>
                <th className="text-left p-3 font-mono uppercase text-xs">Address</th>
                <th className="text-left p-3 font-mono uppercase text-xs">Status</th>
                <th className="text-right p-3 font-mono uppercase text-xs">Amount</th>
              </tr>
            </thead>
            <tbody data-testid="pickup-stops-tbody">
              {loading && (
                <tr><td colSpan={6} className="p-6 text-center text-gray-600 font-mono">Loading & geocoding addresses…</td></tr>
              )}
              {!loading && stops.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-gray-600 font-mono">No pending pickups in {city}.</td></tr>
              )}
              {!loading && stops.map(s => (
                <tr key={s.booking_id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-3 font-mono font-bold">{s.stop_number}</td>
                  <td className="p-3 font-mono text-xs">{s.baggage_tag}</td>
                  <td className="p-3 text-xs">{s.customer_name}</td>
                  <td className="p-3 text-xs text-gray-600 max-w-xs truncate" title={s.pickup_address}>{s.pickup_address}</td>
                  <td className="p-3"><span className="text-[10px] font-mono font-bold uppercase px-2 py-1 border-2 border-black bg-[#FFB800]">{s.tracking_status}</span></td>
                  <td className="p-3 text-right font-mono">{formatInr(displayAmount(s))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ungeocoded warning list */}
      {data?.ungeocoded?.length > 0 && (
        <div className="card-brutalist p-4 bg-amber-50 border-amber-500" data-testid="pickup-ungeocoded">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-amber-700" />
            <h3 className="font-mono uppercase text-sm font-bold text-amber-800">
              {data.ungeocoded.length} bookings could not be mapped
            </h3>
          </div>
          <p className="text-xs text-amber-700 font-mono mb-3">
            These bookings have invalid or missing addresses. Agent dispatch will need to contact the customer directly.
          </p>
          <ul className="text-xs font-mono space-y-1 ml-4 list-disc">
            {data.ungeocoded.slice(0, 10).map(b => (
              <li key={b.booking_id}>
                <b>{b.baggage_tag}</b> · {b.customer_name} · {b.pickup_address || '(no address)'}
              </li>
            ))}
            {data.ungeocoded.length > 10 && (
              <li className="text-amber-600">…and {data.ungeocoded.length - 10} more</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AdminPickupRouteMap;
